import { canv } from "@/canv/global";
import type { Sticker } from "./types";

export interface CachedStickerType extends Sticker {
    imageUrl: string;
    isLoaded: boolean;
    imageElement?: HTMLImageElement;
}

class StickerCache {
    private cache: Map<string, CachedStickerType> = new Map();
    private loadingPromises: Map<string, Promise<HTMLImageElement>> = new Map();
    private isInitialized = false;

    private readonly STICKERS_PATH = "/api/v2/static/media/stickers/";

    /**
     * Инициализирует кэш стикеров
     */
    async initializeStickers(stickers: Sticker[]): Promise<void> {
        if (this.isInitialized) return;

        const loadPromises = stickers.map((sticker) => this.loadSticker(sticker));
        await Promise.all(loadPromises);

        this.isInitialized = true;
    }

    /**
     * Загружает отдельный стикер
     */
    private async loadSticker(sticker: Sticker): Promise<CachedStickerType> {
        const imageUrl = `${canv.host}${this.STICKERS_PATH}${sticker.name}`;
        const cacheKey = sticker.name;

        const cache = this.cache.get(cacheKey);
        if (cache) return cache;

        const cachedSticker: CachedStickerType = {
            ...sticker,
            imageUrl,
            isLoaded: false,
        };

        this.cache.set(cacheKey, cachedSticker);

        // Создаем промис для загрузки изображения
        const loadPromise = this.loadImage(imageUrl);
        this.loadingPromises.set(cacheKey, loadPromise);

        try {
            const imageElement = await loadPromise;
            cachedSticker.imageElement = imageElement;
            cachedSticker.isLoaded = true;
        } catch (error) {
            console.error(`Failed to load sticker ${sticker.name}:`, error);
            cachedSticker.isLoaded = false;
        } finally {
            this.loadingPromises.delete(cacheKey);
        }

        return cachedSticker;
    }

    /**
     * Загружает изображение
     */
    private loadImage(url: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();

            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`Failed to load image: ${url}`));

            img.src = url;
        });
    }

    /**
     * Получает все загруженные стикеры
     */
    getLoadedStickers(): CachedStickerType[] {
        return Array.from(this.cache.values()).filter((sticker) => sticker.isLoaded);
    }

    /**
     * Проверяет, инициализирован ли кэш
     */
    isCacheInitialized(): boolean {
        return this.isInitialized;
    }

    /**
     * Получает URL изображения для стикера
     */
    getImageUrl(stickerName: string): string {
        const sticker = this.cache.get(stickerName);

        return sticker?.imageUrl || `${canv.host}${this.STICKERS_PATH}${stickerName}`;
    }
}

// Создаем единственный экземпляр кэша
export const stickerCache = new StickerCache();
